import { Emoji } from 'emoji-mart';
import React from 'react';
import { TableCellProps } from 'react-virtualized';
import * as yup from 'yup';
import { useFragment } from '@apollo/client';

import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
  OverlayInteractionMode,
} from '$/components/Interactions/types';
import { PageBuilderState } from '$/components/ui/Input/PageBuilder.types';
import config from '$/config';
import {
  AttendeeFragment,
  GetRegistrantsOrderBy,
  InteractionDetails,
  InteractionDetailsFragment,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionOverlaySettings,
  InteractionType,
  InteractionValueType,
  MediaRefFragment,
  PublicEWebinarFragment,
  ReactionFragment,
  ReactionResults,
  ResultsAppearAt,
  RoomType,
} from '$/GraphQL/schema/types';
import {
  FeedbackIcon,
  QuestionIcon,
  QuizIcon,
  TipIcon,
  PrivateMessageIcon,
  ImageOverlayIcon,
  InteractionsIcon,
} from '$/images/FontAwesomeIcons';

import { RenderColumnData } from '$/sections/Home/Dashboard/Registrants/types';
import { getEmoji } from '$/utils/emoji';
import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';
import { DraggableData } from 'react-draggable';
import DangerouslySetInnerHTML from '../ui/EditableInput/DangerouslySetInnerHTML';
import type { TimelineComponents, WebinarPlayerState } from '../WebinarPlayer/context';
import { PlayerMode } from '../WebinarPlayer/types';

import CardMain from './cards/CardMain';
import { AnalyticsCardProps } from './cards/types';
import type { IntegrationInfo } from '$/sections/Home/Profile/TeamIntegrations/Info';

import { InteractionModalTitle } from './Modals/InteractionCardTitlePreview';
import type { InteractionEditableValues, InteractionModalTitleProps } from './types';
import { DefaultWebinarRoom, Rooms, WebinarRoom } from '../WebinarPlayer/Rooms/Rooms';
import { cloneDeep } from '@apollo/client/utilities';
import { apolloClient } from '$/GraphQL/ApolloClient';
import { INTERACTION_REACTION_RESULTS_FRAGMENT } from '$/GraphQL/queries/Interactions';
import { useInteractionReactionResultsQuery } from '$/GraphQL/schema/schema';

export type RibbonMedia = {
  type: string;
  url: string;
  videoUrl?: string | null;
};

export type OverlayComponentProps = {
  interaction: InteractionInfo;
  overlayMode: OverlayInteractionMode;
  displayState: InteractionDisplayState;
  settings?: InteractionOverlaySettings | null;
  isHoveringOverlay?: boolean;
  isInSettings?: boolean;
};

export type AttendeeCardDefaultValues =
  | undefined
  | {
      details: {
        name?: string;
        email?: string;
        phone?: string;
      };
    };

export const NoBodyCard: React.FC = () => {
  return null;
};

export interface AttendeeCardProps {
  onClickHeader?: () => void;
}

export type VideoOverlayTypes = 'over-area' | 'over-video' | 'invisible';

/**
 * TODO: extract title, description, answer from the `content` column of message table
 * The format of content column: <interactedSlackMessage>\n**<interaction-title>**\n<interaction-description>\n**<summaryAnswer>**
 * Note: <interaction-description> maybe have many `\n` characters
 * Ex. Link Interaction: David has responded to a Link.\n**Join whatsapp group**\n**Whatsapp group is to announce updates regarding the event and offer**\n**Clicked**
 */
export const extractAnswerMessageContent = (
  content: string
): {
  interactedMessage: string;
  title: string;
  description: string;
  answer: string;
} => {
  // find first index of \n
  let index = content.indexOf('\n');
  const interactedMessage = content.substring(0, index);
  // remove interacted message out of content string
  content = content.substring(index + 1);
  // find first index of \n
  index = content.indexOf('\n');
  const title = content.substring(0, index);
  // remove title out of content string
  content = content.substring(index + 1);
  // find last index of \n
  index = content.lastIndexOf('\n');
  // extract answer from content string
  const answer = content.substring(index + 1);
  // get description from content string
  const description = content.substring(0, index);
  return {
    interactedMessage,
    title,
    description,
    answer,
  };
};

export const interactionTypeName = (type: string): string | undefined => {
  switch (type) {
    case InteractionType.Poll:
      return `Poll`;

    case InteractionType.Quiz:
      return `Quiz`;

    case InteractionType.Handout:
    case `Link`:
      return `Link`;

    case InteractionType.Question:
      return `Question`;

    case InteractionType.Feedback:
      return `Feedback`;

    case InteractionType.SpecialOffer:
      return `Special offer`;

    case InteractionType.RequestToContact:
      return `Contact form`;

    case InteractionType.Tip:
      return 'Tip';

    case InteractionType.ConversionNotifications:
      return `Conversion alerts`;

    case InteractionType.TextOverlay:
      return 'Text overlay';

    case InteractionType.LogoOverlay:
      return 'Image overlay';

    case InteractionType.PrivateMessage:
      return 'Private message';

    case InteractionType.NextWebinar:
      return 'Next webinar';

    case 'Thinkific-Promo':
      return 'Thinkific offer';

    case 'ProductHunt-Promo':
      return 'Product Hunt badge';

    case InteractionType.Testimonial:
      return 'Testimonial';

    default:
      return undefined;
  }
};

export enum InteractionDisplayState {
  Normal = 'Normal',
  Submitted = 'Submitted',
  Result = 'Result',
}

export type OverlayDimensions = {
  x: number;
  y: number;
  playerWidth: number; // This is recalculated based on scaling or not scaling rendering
  playerHeight: number;
  paddedWidth?: number;
  paddedHeight?: number;
  paddingXpx?: number;
  paddingYpx?: number;
  width?: number;
  height?: number;
  imageSize?: number;
  dynamicSize?: boolean;
};

export type InteractionHeaderModalProps = {
  interaction: InteractionInfo;
  onDelete: () => Promise<void>;
  isDeleting?: boolean;
  onDuplicate: () => Promise<void>;
  isDuplicating?: boolean;
  interactionId?: string;
  onClose?: () => Promise<void>;
};

export type InteractionModalFooterProps = InteractionModalComponentProps & {
  interaction: InteractionInfo;
  state: PageBuilderState;
};

export type PartialInteractionReaction = Pick<
  ReactionFragment,
  'numberAnswer' | 'stringAnswer' | 'arrayAnswer' | 'detailsFields' | 'reactedAt'
>;

export type InteractionAttendeeResults = {
  forAnalytics?: boolean;
  answers?: { answer: string; percent: number }[] | null;
};

export type InteractionAnalyticsResults = {
  forAnalytics?: boolean;
  totalCount?: number;
  totalCorrect?: number; // used for Quiz
  average?: number;
  answers?: { answer: string; count?: number; percent: number }[] | null;
};

export type InvisibleNotificationProps = {
  interaction: InteractionInfo;
  attendee: AttendeeFragment;
};

export abstract class InteractionInfo {
  public rooms: Rooms;
  protected i: InteractionForAttendeeFragment | InteractionFragment;
  public ewebinar?: PublicEWebinarFragment;
  public integration?: IntegrationInfo;
  public attendee?: AttendeeFragment;

  constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    this.i = cloneDeep(i);
    this.ewebinar = ewebinar;
    this.attendee = attendee;
    this.rooms = Rooms.create(ewebinar);

    if ((i.type as string) === 'Interaction') {
      console.error('Invalid interaction type ', i);
    }

    // Cast interactionId to a string
    this.i.id = String(this.i.id);
  }

  public userCanAdd(): boolean {
    return this.typeName() !== undefined;
  }

  public get interaction(): InteractionForAttendeeFragment | InteractionFragment {
    return this.i;
  }

  public get isNew(): boolean {
    return this.i.id.length === 0;
  }

  public get id(): string | undefined {
    return this.i.id.length === 0 ? undefined : String(this.i.id);
  }

  public get shortId(): string {
    return this.i.interactionShortId;
  }

  /*
   * Returns InteractionType the server knows this interaction as
   * This is used due to interactions such as PauseAnchor which is
   * really just an anchor with different defaults
   *
   * DD: This is probably no longer needed given interaction templates
   */
  public serverType(): string | undefined {
    return this.type;
  }

  /*
   * Returns timeline component this interaction uses
   */
  public timelineComponent(): keyof TimelineComponents {
    return 'main';
  }

  public get type(): string {
    return this.i.type;
  }

  public category(): 'Standard' | 'Special' | 'Anchor' | 'Conversion' {
    switch (this.i.type) {
      case InteractionType.Feedback:
      case InteractionType.Poll:
      case InteractionType.Question:
      case InteractionType.SpecialOffer:
      case InteractionType.Quiz:
        return 'Standard';

      case InteractionType.Anchor:
        return 'Anchor';
    }

    return 'Special';
  }

  public hasAnswers(): boolean {
    switch (this.type) {
      case InteractionType.SpecialOffer:
      case InteractionType.Poll:
      case InteractionType.Feedback:
      case InteractionType.Question:
      case InteractionType.Quiz:
        return true;

      default:
        return false;
    }
  }

  public points(): number {
    switch (this.type) {
      case InteractionType.Poll:
      case InteractionType.Feedback:
      case InteractionType.Question:
      case InteractionType.Quiz:
      case InteractionType.RequestToContact:
      case InteractionType.SpecialOffer:
        return 1;

      default:
        return 0;
    }
  }

  public removeCardOnAnswer(): boolean {
    switch (this.type) {
      case InteractionType.SpecialOffer:
      case InteractionType.Handout:
        return false;

      default:
        return this.points() > 0;
    }
  }

  public hasExpiresInFieldToggle(): boolean {
    return false;
  }

  public hasExpiresInField(): boolean {
    return false;
  }

  public hasConditionalDisplay(): boolean {
    return true;
  }

  public hasExpiresInReplayToggle(): boolean {
    return false;
  }

  public showTimer(mode: PlayerMode): boolean {
    const endsIn = this.i.details?.endsIn;
    let hasTimer = Boolean(endsIn);
    if (mode === PlayerMode.replay) {
      hasTimer = false;
    }
    return hasTimer;
  }

  public hasShowTimerToggle(): boolean {
    return false;
  }

  public appearAtBeforeWatchingTitle(): string | null {
    return null;
  }

  public hasEndsAt(): boolean {
    return false;
  }

  public supportsShortcut(): boolean {
    return false;
  }

  public isVisibleToAttendee(): boolean {
    return true;
  }

  public isInteractiveCard(): boolean {
    return this.isVisibleToAttendee();
  }

  public color(): string | undefined {
    return undefined;
  }

  public bgMarkerColor(coloredBg?: boolean): string {
    const bgColor = this.fragment.templateId
      ? '#e68983'
      : this.fragment.isFavorite
        ? '#d69e2e'
        : coloredBg
          ? this.color() ?? this.ewebinar?.primaryColor
          : '#fff';
    return bgColor ?? '#fff';
  }

  public isExpired(playbackPosition: number, mode: PlayerMode): boolean {
    const endIn = this.fragment.details?.endsIn;
    if (!this.hasExpiresInField() || !endIn) return false;

    if (mode === PlayerMode.replay) {
      return Boolean(this.fragment.details?.endInReplay);
    }

    return this.playbackPosition + endIn - playbackPosition <= 0;
  }

  public timeLeft(playbackPosition: number): number {
    const endsIn = this.i.details?.endsIn;
    if (!endsIn) return 0;

    const appearAt = this.playbackPosition;
    const secondsLeft = appearAt + endsIn - playbackPosition;
    return Math.max(secondsLeft, 0);
  }

  public onPrepareValuesForForm(_context: WebinarPlayerState): InteractionEditableValues {
    // Called before InteractionModal initialies ApolloForm - output of this will be defaultValues
    const values: InteractionEditableValues = {
      details: {
        ...this.fragment.details!,
        playbackPosition: this.playbackPosition,
        propertyName: this.propertyName,
        publishAsTemplate: this.fragmentForEdit.templateId ? true : false,
        templateCategories: this.fragmentForEdit.templateCategories,
        templateName: this.fragmentForEdit.templateName,
        templateDesc: this.fragmentForEdit.templateDesc,
        isFavorite: this.fragmentForEdit.isFavorite,
        templateDetails: this.fragmentForEdit.templateDetails || undefined,
        displayRules: this.fragment.details?.displayRules ?? [],
      },
    };

    // Make default button text Submit for backward compatibility
    values.details.buttonText = values.details.buttonText ?? 'Submit';

    return values;
  }

  public attendeeCardDefaultValues(_attendee?: AttendeeFragment): AttendeeCardDefaultValues {
    return undefined;
  }

  public async onSave(values: InteractionEditableValues): Promise<InteractionEditableValues> {
    // Called before InteractionModal calls backend to update/create interaction
    return values;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      title: yup.string().trim().required(),
      description: yup.string().trim().required(),
    };
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    switch (this.type) {
      case InteractionType.Feedback:
        return yup.object().shape({
          numberAnswer: yup.number().required(),
        });

      case InteractionType.Question:
        return yup.object().shape({
          stringAnswer: yup.string().required(),
        });

      default:
        return undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public icon(): React.FC<any> {
    switch (this.type) {
      case InteractionType.Quiz:
        return QuizIcon;

      case InteractionType.Question:
        return QuestionIcon;

      case InteractionType.Feedback:
        return FeedbackIcon;

      case InteractionType.Tip:
        return TipIcon;

      case InteractionType.LogoOverlay:
        return ImageOverlayIcon;

      case InteractionType.PrivateMessage:
        return PrivateMessageIcon;

      default:
        return InteractionsIcon;
    }
  }

  public typeName(): string | undefined {
    return interactionTypeName(this.type);
  }

  public static nonEditableTypes: string[] = [
    InteractionType.Welcome,
    InteractionType.EndStream,
    InteractionType.QuizSummary,

    ...(config.isProduction ? [] : []),
  ];

  public isEditable(): boolean {
    // TODO: We will want to have different type of editable moving forward I suspect
    // a. Can add it, see it on timeline, move it around & delete it
    // b. All of a AND can edit the interaction in Modal
    // c. Can't do a or b

    return !InteractionInfo.nonEditableTypes.includes(this.type);
  }

  public detailsKey(): string | undefined {
    switch (this.type) {
      case InteractionType.NextWebinar:
        return 'nextWebinar';
      default:
        return undefined;
    }
  }

  public title(): string {
    switch (this.i.type) {
      case InteractionType.Feedback:
        return this.i.details?.title ?? 'Give us your feedback!';

      case InteractionType.NextWebinar:
        return this.i.details?.title || 'Register for the next webinar';

      default:
        return this.i.details?.title || this.typeName() || '';
    }
  }

  public templateName(): string {
    return this.fragment.templateName || this.i.details?.title || this.type;
  }

  public subtitle(): string | undefined {
    return this.i.details?.subtitle ?? undefined;
  }

  public description(): string {
    switch (this.i.type) {
      case InteractionType.TextOverlay:
        return this.title();
      case InteractionType.LogoOverlay:
        return this.i.details?.description || this.typeName() || '';
      case InteractionType.PrivateMessage:
        return this.i.details?.description || 'Hey { firstName }! Got any questions for me so far?';
      default:
        return this.i.details?.description || '';
    }
  }

  public answerMessage(
    reaction: Pick<ReactionFragment, 'stringAnswer' | 'numberAnswer' | 'detailsFields'>
  ): string {
    return reaction.stringAnswer ?? '';
  }

  public isSingleButtonCard(): boolean {
    switch (this.type) {
      case InteractionType.Handout:
      case InteractionType.SpecialOffer:
      case 'Thinkific-Promo':
      case 'ProductHunt-Promo':
        return true;
    }

    return false;
  }

  public messageCardBody(
    _reaction: Pick<
      ReactionFragment,
      'stringAnswer' | 'numberAnswer' | 'detailsFields' | 'arrayAnswer'
    >,
    answerContent?: string
  ): JSX.Element | null {
    const cardBody: JSX.Element | null = <DangerouslySetInnerHTML markdownString={answerContent} />;

    return cardBody;
  }

  public renderMessageCard(
    reaction: Pick<
      ReactionFragment,
      'stringAnswer' | 'numberAnswer' | 'detailsFields' | 'arrayAnswer'
    >,
    content: string
  ): JSX.Element {
    const { title, description, answer } = extractAnswerMessageContent(content);

    const cardBody = this.messageCardBody(reaction, answer);

    return (
      <CardMain.InteractionMessageCard
        interaction={this}
        title={title}
        description={description}
        cardBody={cardBody}
      />
    );
  }

  public modalTitleComponent(): React.FC<InteractionModalTitleProps> | null {
    switch (this.i.type) {
      case InteractionType.PrivateMessage:
        return null;
      default:
        return InteractionModalTitle;
    }
  }

  public abstract useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null;

  public abstract modalSettingsAccordions(): InteractionModalAccordionsHook | null;

  public modalFooterComponent(): React.FC<InteractionModalFooterProps> | null {
    return null;
  }

  public abstract analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>>;

  public cardTitleMedia(): MediaRefFragment | RibbonMedia[] | undefined | null {
    return this.details?.imageMediaRef;
  }

  public cardComponent(): React.FC<AttendeeCardProps> {
    return CardMain.StandardCard;
  }

  public abstract cardBodyComponent(): React.FC | React.LazyExoticComponent<React.FC>;

  public notificationComponentForAttendee(): React.FC<InvisibleNotificationProps> | null {
    return null;
  }

  public valueType(): InteractionValueType {
    return this.i.valueType;
  }

  public onlyOneInstance(): boolean {
    return false;
  }

  public minRange(): number {
    return 0;
  }

  public maxRange(): number {
    switch (this.i.type) {
      case InteractionType.Feedback:
        return 5;

      default:
        return 0;
    }
  }

  public get details(): InteractionDetailsFragment | undefined | null {
    return this.i.details;
  }

  public detailsDefault(_interactions: InteractionInfo[]): InteractionDetails {
    return {
      title: this.title(),
      description: this.description(),
    };
  }

  public detailsDuplicateDefault(_interactions: InteractionInfo[]): Partial<InteractionDetails> {
    return {};
  }

  private get resultsFragmentOptions() {
    return {
      id: `Interaction:${this.id}`,
      fragmentName: 'InteractionReactionResults',
      fragment: INTERACTION_REACTION_RESULTS_FRAGMENT,
      from: {
        id: this.id,
        __typename: 'Interaction',
      },
    };
  }

  public results(): InteractionAttendeeResults | undefined | null {
    // read from the fragment of Apollo cache
    const fragment: { results: ReactionResults } | null = apolloClient.cache.readFragment(
      this.resultsFragmentOptions
    );
    return fragment?.results ?? undefined;
  }

  public useInteractionReactionResults = (
    skip = false
  ): InteractionAttendeeResults | undefined | null => {
    // watch the fragment of Apollo cache so it always up to date
    useFragment(this.resultsFragmentOptions);

    const results = this.results();
    // trigger the query to get the results incase attendee has already submitted the interaction
    useInteractionReactionResultsQuery({
      variables: {
        id: this.id!,
      },
      skip: Boolean(skip || !this.id || results),
    });
    return results;
  };

  public isResultsCard(): boolean {
    return this.shortId?.endsWith('-result');
  }

  public get reaction(): undefined | null | PartialInteractionReaction {
    return this.fragment.reaction;
  }

  public appearAtPlaybackPosString(): string {
    return formatTimeInWebinarSecs(this.playbackPosition) || '';
  }

  public get propertyName(): string | undefined {
    return this.fragment.propertyName ?? undefined;
  }

  public get playbackPosition(): number {
    return this.rooms?.playbackPositionFromDuo(this.i.appearAt, this.i.room) ?? 0;
  }

  public get endsAtPlaybackPosition(): number {
    return this.hasEndsAt()
      ? this.rooms?.playbackPositionFromDuo(this.endsAt()!, RoomType.Presentation) ?? 0
      : this.rooms?.getRoom(RoomType.Exit).endsAt ?? Number.MAX_SAFE_INTEGER;
  }

  public get room(): WebinarRoom {
    return this.rooms?.getRoomAt(this.playbackPosition) ?? DefaultWebinarRoom;
  }

  public get fragment(): InteractionForAttendeeFragment {
    return this.i;
  }

  public get fragmentForEdit(): InteractionFragment {
    return this.i;
  }

  public updateFragment(fragment: InteractionFragment | InteractionForAttendeeFragment): void {
    this.i = fragment;
  }

  public hasResultsToShow(): boolean {
    switch (this.i.type) {
      case InteractionType.Poll:
      case InteractionType.Quiz:
        return true;
    }

    return false;
  }

  public hasPropertyNameField(): boolean {
    return (
      this.valueType() != InteractionValueType.None &&
      (!this.hasVideoOverlay() || this.isInteractiveCard())
    );
  }

  public get hasReaction(): boolean {
    return Boolean(this.fragment.reaction);
  }

  public hasResultsAppearAtField(): boolean {
    switch (this.i.type) {
      case InteractionType.Poll:
      case InteractionType.Quiz:
        return true;
      default:
        return false;
    }
  }

  public endsAt(): number | undefined {
    return this.i?.details?.endsAt ?? undefined;
  }

  public isAnswered(): boolean {
    return Boolean(this.reaction);
  }

  public shouldRemoveAnsweredCard(): boolean {
    if (!this.isAnswered() || !this.removeCardOnAnswer()) return false;
    return true;
  }

  public cardDisplayState(_playbackPosition: number, mode: PlayerMode): InteractionDisplayState {
    if (mode === PlayerMode.edit) {
      return InteractionDisplayState.Normal;
    }

    if (this.reaction) {
      return InteractionDisplayState.Submitted;
    }

    return InteractionDisplayState.Normal;
  }

  public displayLocation(): 'top' | 'bottom' {
    return 'bottom';
  }

  public appearAt(): number {
    return this.fragment.appearAt;
  }

  public resultsAppearAtPlaybackPos(): number {
    switch (this.i.details?.resultsAppearAt) {
      case ResultsAppearAt.Immediately:
        return this.rooms.domain[0];

      case ResultsAppearAt.EndOfPresentation:
        return this.rooms.getRoom(RoomType.Presentation).duration - 1;

      case ResultsAppearAt.AfterXMinutes: {
        const endIn = this.i.details?.endsIn ?? 0;
        return Math.min(
          this.appearAt() + endIn,
          this.rooms.getRoom(RoomType.Presentation).duration
        );
      }

      case ResultsAppearAt.Never:
      default:
        return this.rooms.domain[1] + 1000;
    }
  }

  public renderColumnData(data: RenderColumnData): RenderColumnData | null {
    switch (this.type) {
      case InteractionType.Feedback:
        return {
          ...data,
          orderBy: GetRegistrantsOrderBy.FeedbackRating,
          cellRenderer: ({ cellData }: TableCellProps): React.ReactNode => {
            const reaction = cellData as ReactionFragment;
            if (!reaction?.numberAnswer) return <div />;
            return (
              <div className='flex items-center w-full'>
                <Emoji emoji={getEmoji(reaction.numberAnswer)} set='apple' size={21} />
                <span className='ml-2'>{reaction.numberAnswer} / 5</span>
                {reaction.stringAnswer && (
                  <span className='ml-2'>{this.answerMessage(reaction)}</span>
                )}
              </div>
            );
          },
        };

      case InteractionType.Question:
        return {
          ...data,
          flexGrow: 2,
          width: 360,
          cellRenderer: ({ cellData }: TableCellProps): React.ReactNode => {
            const reaction = cellData as ReactionFragment;
            if (!reaction?.stringAnswer) return <div />;
            return <div className='pre-wrap'>{reaction.stringAnswer}</div>;
          },
        };

      default:
        return null;
    }
  }
  public hasPinToTopToggle(): boolean {
    return true;
  }

  public isPinned(): boolean {
    return Boolean(this.details?.isSticky);
  }

  public hasNotifyModeratorToggle(): boolean {
    switch (this.i.type) {
      case InteractionType.Question:
      case InteractionType.Poll:
        return true;
    }

    return false;
  }

  public onlyExistsInPresentationRoom(): boolean {
    return false;
  }

  public hideOnMobile(): boolean {
    return Boolean(this.i.details?.hideOnMobile);
  }

  public renderNotification(_mode?: OverlayInteractionMode): JSX.Element | null {
    return null;
  }

  public hasCard(_isPreview?: boolean): boolean {
    return true;
  }

  /* Surface whether the interaction uses an IFrame as this gets in the way of scroll */
  public hasIFrame(): boolean {
    switch (this.i.type) {
      case InteractionType.TipWithKoFi:
        return true;
    }

    return false;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return null;
  }

  public hasEngagement(): boolean {
    return true;
  }

  public hasAnswerValue(): boolean {
    return false;
  }

  public isInteractiveOverlay(): boolean {
    return Boolean(this.details?.link);
  }

  public staticOverlayPosition(): boolean {
    return false;
  }

  public showOverlayEditor(): boolean {
    return this.hasVideoOverlay() !== null;
  }

  public overlaySettings(): InteractionOverlaySettings | undefined {
    return this.fragment.details?.overlaySettings ?? undefined;
  }

  public renderOverlayComponent(): React.FC<OverlayComponentProps> {
    return (): JSX.Element | null => {
      return null;
    };
  }

  public showOverlayButton(): boolean {
    // temporarily hide it until new attendee UI is done
    // return Boolean(this.fragment.details?.showOverlayButton);
    return false;
  }

  public requiresHeaderMedia(): boolean {
    return false;
  }

  public calcOverlayPositionAndAnchor(
    data: DraggableData,
    state: {
      playerWidthScaled: number;
      videoAspect: number;
    }
  ): [number, number] {
    return [
      (data.lastX / state.playerWidthScaled) * 100,
      (data.lastY / (state.playerWidthScaled / state.videoAspect)) * 100,
    ];
  }

  public calcOverlayDimensionsHook(): (
    interaction: InteractionInfo,
    playerWidth: number,
    playerHeight: number,
    settings?: InteractionOverlaySettings | null
  ) => OverlayDimensions {
    return (
      _interaction: InteractionInfo,
      playerWidth: number,
      playerHeight: number,
      settings?: InteractionOverlaySettings | null
    ): OverlayDimensions => {
      const imageSize = settings?.imageSize ?? 0;

      let width = settings?.width ?? 0;
      let height = settings?.height ?? 0;

      const aspectRatio = width / height;
      width = (imageSize / 100) * playerWidth;
      height = width / aspectRatio;

      const paddingXpx = Math.min(Math.max(5, width * 0.2), Math.max(5, height * 0.2));
      const paddingYpx = paddingXpx;

      const paddedWidth = paddingXpx + width;
      const paddedHeight = paddingYpx + height;

      const position = settings?.position
        ? [settings.position[0], settings.position[1]]
        : [
            ((playerWidth - paddedWidth * 1.1) / playerWidth) * 100,
            ((playerHeight - paddedHeight * 1.1) / playerHeight) * 100,
          ];

      return {
        x: (position[0] / 100) * playerWidth,
        y: (position[1] / 100) * playerHeight,
        width,
        height,
        paddingXpx,
        paddingYpx,
        paddedWidth,
        paddedHeight,
        playerWidth,
        playerHeight,
        imageSize,
      };
    };
  }

  public canSubmitMuktipleTimes(): boolean {
    return false;
  }
}

export const getInteractionsHash = (interactions: InteractionInfo[]): string => {
  const result = interactions.map((i) => {
    const timeHash = new Date(i.interaction.updatedAt).getTime();
    return `${i.id}-${timeHash}-${i.reaction?.reactedAt ?? ''}`;
  });
  return result.join(',');
};
